/**
 * Five-bucket waterfall of net project revenue R — exact mirror of contracts/src/libraries/WaterfallLib.sol.
 * Each non-holder bucket rounds down; the rounding residual goes to the holder allocation, so every split sums to R.
 */
import type { WaterfallPolicy } from './types.js';

const BPS = 10_000n;

export interface WaterfallSplit {
  holdersDirect: bigint;
  holdersDeferred: bigint;
  forge: bigint;
  road: bigint;
  prize: bigint;
  operations: bigint;
}

export function validatePolicy(p: WaterfallPolicy): boolean {
  return p.holdersBps + p.forgeBps + p.roadBps + p.prizeBps + p.opsBps === BPS && p.holderDeferredBps <= BPS &&
    [p.holdersBps, p.forgeBps, p.roadBps, p.prizeBps, p.opsBps, p.holderDeferredBps].every((v) => v >= 0n);
}

export function waterfallSplit(r: bigint, p: WaterfallPolicy): WaterfallSplit {
  const forge = (r * p.forgeBps) / BPS;
  const road = (r * p.roadBps) / BPS;
  const prize = (r * p.prizeBps) / BPS;
  const operations = (r * p.opsBps) / BPS;
  const holders = r - forge - road - prize - operations;
  const holdersDeferred = (holders * p.holderDeferredBps) / BPS;
  return { holdersDirect: holders - holdersDeferred, holdersDeferred, forge, road, prize, operations };
}

/**
 * Outcome of RunnerTreasury.reserveRun (and so of RunnerGame.requestCourse) for given reserves and road parameters.
 *   - funded: the road reserve covers `minBudget` (and is not empty) and operations cover `provision`; the run reserves
 *     min(desired, roadReserve) and the provision.
 *   - exhibition (D48): not funded, but `minBudget == 0`: the run starts anyway with budget 0 (no road money moves); the
 *     provision is charged only when operations cover it, otherwise it is 0 (the keeper is unpaid for that run).
 *   - neither: requestCourse reverts NotFunded (warm-up) and nothing moves.
 */
export interface RunReservation {
  /** requestCourse would succeed (funded or exhibition). */
  startable: boolean;
  funded: boolean;
  exhibition: boolean;
  /** Road budget reserved (0 for an exhibition run or warm-up). */
  budget: bigint;
  /** Execution provision moved from operations (0 in warm-up, and for an exhibition run operations cannot cover). */
  provision: bigint;
}

export function runReservation(roadReserve: bigint, operations: bigint, desired: bigint, minBudget: bigint, provision: bigint): RunReservation {
  const avail = roadReserve < desired ? roadReserve : desired;
  if (avail === 0n || avail < minBudget || operations < provision) {
    if (minBudget !== 0n) return { startable: false, funded: false, exhibition: false, budget: 0n, provision: 0n };
    return { startable: true, funded: false, exhibition: true, budget: 0n, provision: operations < provision ? 0n : provision };
  }
  return { startable: true, funded: true, exhibition: false, budget: avail, provision };
}

/**
 * Road budget arithmetic of RunnerTreasury.reserveRun: what a run would be funded with. 0 = not funded: warm-up when
 * `minBudget > 0`, an exhibition run (budget 0, D48) when `minBudget == 0` — see `runReservation`.
 */
export function fundableBudget(roadReserve: bigint, operations: bigint, desired: bigint, minBudget: bigint, provision: bigint): bigint {
  return runReservation(roadReserve, operations, desired, minBudget, provision).budget;
}
